import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import ServiceButtons from '../../components/feature/ServiceButtons';
import { useEffect } from 'react';

export default function Process() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Our Appraisal Process - Virtus Realty Appraisal Services LLC';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Discover our comprehensive 5-step property appraisal process: consultation, inspection, market analysis, valuation, and report delivery. Professional and transparent service in New Jersey.');
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'appraisal process, property inspection, market analysis, valuation report, appraisal consultation, New Jersey');
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Property Appraisal Process",
      "description": "Our comprehensive 5-step property appraisal process",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Request & Consultation",
          "text": "Contact us to discuss your appraisal needs and receive a customized quote"
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Property Inspection",
          "text": "Our certified appraiser conducts a thorough on-site inspection of the property"
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Research & Market Analysis",
          "text": "We analyze comparable sales, market trends, and property characteristics"
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "Valuation & Report Preparation",
          "text": "We prepare a comprehensive appraisal report with detailed analysis and valuation"
        },
        {
          "@type": "HowToStep",
          "position": 5,
          "name": "Review & Delivery",
          "text": "Final review and delivery of your certified appraisal report"
        }
      ]
    });
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const processSteps = [
    {
      step: '01',
      icon: 'ri-file-text-line',
      title: 'Request & Consultation',
      description: 'Clients begin by submitting a simple online request or contacting our office. We review the property type, purpose of the appraisal, and any specific requirements or deadlines.'
    },
    {
      step: '02',
      icon: 'ri-search-line',
      title: 'Property Inspection',
      description: 'A Certified General Appraiser personally inspects the property, verifying physical characteristics, condition, and relevant site details. For complex or commercial assignments, we coordinate access with all necessary parties.'
    },
    {
      step: '03',
      icon: 'ri-bar-chart-line',
      title: 'Research & Market Analysis',
      description: 'We collect and analyze market data, comparable sales, income, and cost factors appropriate to the property type — whether residential, commercial, or industrial.'
    },
    {
      step: '04',
      icon: 'ri-calculator-line',
      title: 'Valuation & Report Preparation',
      description: 'Using accepted appraisal methodologies and verified market data, we develop a defensible opinion of value tailored to the property and the purpose of the assignment.'
    },
    {
      step: '05',
      icon: 'ri-shield-check-line',
      title: 'Review & Delivery',
      description: 'Each report undergoes an internal quality review before delivery. Clients receive a secure electronic copy and may request consultation to discuss conclusions or next steps.'
    },
    {
      step: '06',
      icon: 'ri-customer-service-line',
      title: 'Follow-Up & Support',
      description: 'We remain available for clarifications, updates, or future appraisal needs. Long-term relationships and client confidence are at the core of our practice.'
    }
  ];

  const valuationApproaches = [
    {
      icon: 'ri-exchange-line',
      title: 'Sales Comparison Approach',
      description: 'Analyzing comparable sales of similar properties to determine market value based on recent transactions.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Income Capitalization Approach',
      description: 'Evaluating the property\'s income potential and market return for investment properties.'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Cost Approach',
      description: 'Estimating the cost to replace the property, less depreciation, plus land value.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/2edfeb77aa79d2e0d37a7ce55a84f8c4.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight font-elegant">
            Our Proven <span className="text-gradient-gold">Appraisal Process</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto leading-relaxed font-modern">
            Accurate. Efficient. Professional.
          </p>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto font-modern">
            Every valuation follows a disciplined, transparent process designed to ensure accuracy, compliance, and client trust
          </p>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-4 font-elegant">
              Six Steps to Accurate Valuations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-modern">
              Our systematic approach ensures every appraisal meets the highest standards of accuracy and compliance
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative bg-white rounded-xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className="absolute -top-4 left-8">
                  <div className="w-12 h-12 gradient-gold rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-black-900 font-elegant">{step.step}</span>
                  </div>
                </div>
                <div className="pt-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${step.icon} text-gold-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-black-900 mb-4 font-elegant">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 font-modern leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valuation Approaches Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-4 font-elegant">
              Three Recognized Approaches to Value
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto font-modern">
              Depending on the property type and scope, we apply one or more of the three recognized approaches to value, ensuring comprehensive and defensible valuations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {valuationApproaches.map((approach, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="w-16 h-16 gradient-gold rounded-xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${approach.icon} text-black-900 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-black-900 mb-4 font-elegant">
                  {approach.title}
                </h3>
                <p className="text-gray-600 font-modern leading-relaxed">
                  {approach.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
                USPAP Compliant Reports
              </h2>
              <p className="text-lg text-gray-600 mb-6 font-modern leading-relaxed">
                Every report complies with USPAP and relevant regulatory standards, ensuring accuracy, consistency, and credibility. Our certified appraisers follow strict professional guidelines to deliver defensible valuations.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-shield-check-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Uniform Standards of Professional Appraisal Practice</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-file-shield-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Regulatory Compliance Assurance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-award-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Certified General Appraiser Standards</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-lock-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Secure Electronic Delivery</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20USPAP%20compliance%20documents%20and%20certification%20materials%20on%20elegant%20desk%20with%20classical%20architectural%20elements%2C%20gold%20and%20black%20color%20scheme%2C%20sophisticated%20legal%20and%20regulatory%20documentation%20setting&width=600&height=500&seq=compliance&orientation=landscape"
                alt="USPAP compliant appraisal reports and certification documents"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Property Types Section */}
      <section className="py-20 bg-black-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-elegant">
              All Property Types Covered
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-modern">
              Our process adapts to the unique requirements of each property type while maintaining consistent standards of excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
              <div className="w-16 h-16 gradient-gold rounded-xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-home-line text-black-900 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4 font-elegant">
                Residential Properties
              </h3>
              <p className="text-gray-300 font-modern">
                Single-family homes, condominiums, multi-family dwellings, and new construction valuations
              </p>
            </div>
            
            <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
              <div className="w-16 h-16 gradient-gold rounded-xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-black-900 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4 font-elegant">
                Commercial Properties
              </h3>
              <p className="text-gray-300 font-modern">
                Office buildings, retail spaces, mixed-use developments, and investment properties
              </p>
            </div>
            
            <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
              <div className="w-16 h-16 gradient-gold rounded-xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-4-line text-black-900 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4 font-elegant">
                Industrial Properties
              </h3>
              <p className="text-gray-300 font-modern">
                Warehouses, manufacturing facilities, distribution centers, and special purpose properties
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
              Ready to Begin Your Appraisal?
            </h2>
            <p className="text-xl text-gray-600 mb-8 font-modern">
              At Virtus Realty Appraisal Services LLC, every valuation follows a disciplined, transparent process designed to ensure accuracy, compliance, and client trust.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                  <i className="ri-file-text-line mr-2"></i>
                  Request an Appraisal
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-gold-500 text-gold-600 hover:bg-gold-500 hover:text-black-900 font-semibold">
                  <i className="ri-service-line mr-2"></i>
                  View Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ServiceButtons />
    </div>
  );
}
